<template>
  <div class="account__subcontainer">
    <h1 class="account__title h2 mb-2">{{ $t('login.title') }}</h1>

    <p class="text-color-secondary-2 text-small mb-2">{{ $t('login.p') }}</p>

    <form id="account-login" @submit="login">
      <div class="account__input account__input--email">
        <form-input
          id="input-email-register"
          :value="email"
          type="email"
          :label="$t('account.labels.email')"
          label-visible
          :placeholder="$t('account.placeholder.email')"
          readonly
        >
          <a href="#" class="input__icon" @click.prevent="changeMail">
            <span class="visually-hidden">{{ $t('action.edit') }}</span>
            <iconEdit />
          </a>
        </form-input>
      </div>

      <div class="account__input mt-2">
        <form-input
          id="input-email-register-pwd"
          ref="inputPassword"
          v-model="password"
          :type="passwordVisible ? 'text' : 'password'"
          :label="$t('account.labels.password')"
          label-visible
          :placeholder="$t('login.placeholder.password')"
          autocomplete="current-password"
          required
          first-submit
          :input-errors="apiErrors"
        >
          <a
            href="#"
            class="input__icon"
            @click.prevent="changeVisibilityPassword"
          >
            <icon
              :name="passwordVisible ? 'eye-slash' : 'eye'"
              :alt="
                $t(
                  passwordVisible ? 'action.hidePassword' : 'action.seePassword'
                )
              "
            />
          </a>
        </form-input>
      </div>

      <div class="d-flex justify-space-between align-start flex-wrap">
        <input-checkbox
          v-if="!isRemembered"
          id="remember"
          v-model="rememberEmail"
          name="remember-me"
        >
          {{ $t('login.rememberMe') }}
        </input-checkbox>
        <transition name="fade-from-top" mode="out-in">
          <button-link
            v-if="forgotLinkSended === false"
            class="text-small pt-0-2"
            @click="sendForgotPswdLink"
          >
            {{ $t('login.forgotPasswordLink') }}
          </button-link>
          <p v-else class="text-small code__sended mb-0">
            {{ $t('login.forgotPasswordLinkSent') }}
          </p>
        </transition>
      </div>

      <p v-if="errors.forgotPassword" class="text-color-error">
        {{ errors.forgotPassword }}
      </p>

      <div class="mt-2">
        <primary-button
          type="submit"
          :text="$t('login.sendButton')"
          align="center"
          :loading="loading"
          :success="success"
          :disabled="loading === true && success === true"
          with-arrow
        />
      </div>

      <div v-if="unexpectedError !== false" class="invalid__field">
        {{ $t('register.error.unexpectedError') }}
      </div>
    </form>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapGetters } from 'vuex'

import FormInput from '@/components/form/Input'
import InputCheckbox from '@/components/form/InputCheckbox'
import PrimaryButton from '@/components/buttons/Primary'
import IconEdit from '@/components/icons/Edit'
import Icon from '@/components/icons/Icon'
import ButtonLink from '@/components/buttons/Link'

import services from '@/services'
import { COOKIE_EMAIL_KEY } from '@/const'
import { EVENTS } from '@/const/events'

export default {
  name: 'Login',
  components: {
    FormInput,
    PrimaryButton,
    ButtonLink,
    IconEdit,
    Icon,
    InputCheckbox,
  },
  props: {
    email: { type: String, required: true },
    isRemembered: { type: Boolean, default: false },
  },

  data() {
    return {
      rememberEmail: false,
      password: '',
      passwordVisible: false,
      firstName: '',
      lastName: '',
      loading: false,
      forgotLinkSended: false,
      success: false,
      unexpectedError: false,
      service: null,
      errors: {
        forgotPassword: null,
      },
    }
  },

  head() {
    return {
      title: `${this.$t('login.title')} - Asphalte`,
    }
  },

  computed: {
    ...mapGetters({
      customer: 'auth/customer',
      authenticated: 'auth/authenticated',
      apiErrors: 'auth/errors',
    }),

    passwordType() {
      return this.passwordVisible === true ? 'text' : 'password'
    },
  },
  mounted() {
    this.$refs.inputPassword.focus()

    history.pushState(null, null, location.href)
    window.onpopstate = () => {
      this.loading = false
      this.forgotLinkSended = false
      this.success = false
      this.clearApiErrors()
      this.unexpectedError = false
      this.$emit('go-back-event')
    }

    this.$track.page(
      { title: this.$t('login.title') },
      this.$route,
      this.$nuxt.context.from
    )
  },

  methods: {
    ...mapActions({
      setConnectedEmail: 'subscriptions/setConnectedEmail',
      connectCustomer: 'auth/connectCustomer',
    }),

    ...mapMutations({
      setApiErrors: 'auth/ERRORS',
      clearApiErrors: 'auth/CLEAR_ERRORS',
    }),

    checkErrors(customerResponse) {
      const apiErrors = []
      if (
        customerResponse.customerUserErrors &&
        customerResponse.customerUserErrors.length > 0
      ) {
        customerResponse.customerUserErrors.forEach((err) => {
          apiErrors.push(err)
        })
        this.setApiErrors(apiErrors)
      }
    },

    changeMail() {
      this.loading = false
      this.forgotLinkSended = false
      this.removeCookieRememberEmail()
      this.$emit('go-back-event')
    },

    login(event) {
      if (event) {
        event.preventDefault()
      }
      this.loading = true
      this.clearApiErrors()
      this.connectCustomer({ email: this.email, password: this.password })
        .then(() => {
          if (this.authenticated) {
            this.success = true

            this.setCookieRememberEmail()
            this.setConnectedEmail(this.email)

            this.$track.login(this.customer)
            this.$logError.identify(this.customer)

            this.$nuxt.$emit(EVENTS.USER_LOGGED_IN)

            this.$emit('redirect-event')
          }
        })
        .catch((err) => {
          this.unexpectedError = true
          this.$logError.captureException(err)
        })
        .finally(() => {
          this.loading = false
        })
    },

    sendForgotPswdLink() {
      this.errors.forgotPassword = null

      if (this.email) {
        this.clearApiErrors()
        services.customerService
          .sendResetPassword(this.email)
          .then((res) => {
            if (res.data.errors && res.data.errors.length > 0) {
              this.errors.forgotPassword = res.data.errors[0].message
              return
            }

            this.checkErrors(res.data.data.customerRecover)
            if (this.apiErrors.length === 0) {
              this.forgotLinkSended = true
            }
          })
          .catch((err) => {
            this.$logError.captureException(err)
            this.loading = false
            this.unexpectedError = true
          })
      }
    },

    changeVisibilityPassword() {
      this.passwordVisible = !this.passwordVisible
    },

    setCookieRememberEmail() {
      if (!this.isRemembered && this.rememberEmail) {
        this.$cookies.set(COOKIE_EMAIL_KEY, this.email, { expires: '1y' })
      }
    },

    removeCookieRememberEmail() {
      if (this.$cookies.isKey(COOKIE_EMAIL_KEY)) {
        this.$cookies.remove(COOKIE_EMAIL_KEY)
      }
    },
  },
}
</script>

<style lang="scss">
.code__sended {
  color: RGB(5 165 89);
}
</style>
